import PropTypes from 'prop-types';
import React from 'react';
/* COMPONENTS */
import InputField from '../../Input/InputField/InputField';
import Tag from '../Tag/Tag';
/* STYLESHEETS */
import './MultiSelectDropdown.scss';

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export default class MultiSelectDropdown extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            isDropdownOpen: false,
            inputValue: '',
            suggestions: props.dropdownOptions,
            selectedOptions: props.selectedOptions,
            selectedOptionIndex: 0,
        };
    }

    static propTypes = {
        /**
         * array of dropdown options
         */
        dropdownOptions: PropTypes.arrayOf(
            /**
             * object must be in this format: { label, value }
             */
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.string,
            })
        ),
        /**
         * boolean to show dropdown or not
         */
        shouldShowDropdown: PropTypes.bool,
        /**
         * function for when option is selected
         */
        onChange: PropTypes.func,
        /**
         * function to set error
         */
        onError: PropTypes.func,
        /**
         * array of selected options from parent
         */
        selectedOptions: PropTypes.array,
    };

    static defaultProps = {
        dropdownOptions: [],
        shouldShowDropdown: true,
    };

    componentDidUpdate = (previousProps) => {
        if (
            previousProps.selectedOptions.length !==
            this.props.selectedOptions.length
        ) {
            this.setState({ selectedOptions: this.props.selectedOptions });
        }
    };

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    }

    setWrapperRef = (node) => {
        this.wrapperRef = node;
    };

    filterOptions = ({ label }) => {
        const typedValue = this.state.inputValue.toLowerCase();

        return label.toLowerCase().indexOf(typedValue) !== -1;
    };

    removeOption = (removedOption) => ({ value }) => removedOption !== value;

    handleChange = (inputValue) => {
        this.props.onError('');

        this.setState({ inputValue }, this.handleFilter);
    };

    handleOpenDropdown = () => this.setState({ isDropdownOpen: true });

    handleFilter = () => {
        if (!!this.props.dropdownOptions.length) {
            const suggestions = this.props.dropdownOptions.filter(
                this.filterOptions
            );
            this.setState({ suggestions });
        }
    };

    handleOptionClick = (label, value) => () => {
        const isNewlyAdded = this.state.selectedOptions.every(
            this.removeOption(value)
        );

        if (!isNewlyAdded) {
            this.props.onError('Job type has already been selected.');
            this.setState({ isDropdownOpen: false });
            return;
        }

        const selectedOptions = [
            ...this.state.selectedOptions,
            { label, value },
        ];

        this.props.onError('');
        this.props.onChange(selectedOptions);

        this.setState({
            selectedOptions,
            isDropdownOpen: false,
            inputValue: '',
            selectedOptionIndex: 0,
        });
    };

    handleOptionRemove = (value) => () => {
        const selectedOptions = this.state.selectedOptions.filter(
            this.removeOption(value)
        );

        this.props.onChange(selectedOptions);
        this.props.onError('');

        this.setState({ selectedOptions });
    };

    handleClickOutside = (event) => {
        if (
            this.state.isDropdownOpen &&
            this.wrapperRef &&
            !this.wrapperRef.contains(event.target)
        ) {
            this.setState({ isDropdownOpen: false });
        }
    };

    handleKeyDown = (keyCode) => {
        const { selectedOptionIndex, suggestions } = this.state;
        const { dropdownOptions } = this.props;

        switch (keyCode) {
            case ENTER_KEY: {
                const { label, value } = dropdownOptions[selectedOptionIndex];
                this.handleOptionClick(label, value)();
                break;
            }
            case UP_KEY: {
                if (selectedOptionIndex === 0) {
                    return;
                }

                this.setState({ selectedOptionIndex: selectedOptionIndex - 1 });
                break;
            }
            case DOWN_KEY: {
                if (selectedOptionIndex === suggestions.length - 1) {
                    return;
                }

                this.setState({ selectedOptionIndex: selectedOptionIndex + 1 });
                break;
            }
            default:
                break;
        }
    };

    handleMouseOver = (index) => () => {
        this.setState({ selectedOptionIndex: index });
    };

    renderDropdownItem = ({ label, value }, index) => {
        const activeCss =
            index === this.state.selectedOptionIndex ? 'active' : '';
        return (
            <li
                key={`suggestion-${index}`}
                onClick={this.handleOptionClick(label, value)}
                className={activeCss}
                onMouseOver={this.handleMouseOver(index)}
            >
                {label}
            </li>
        );
    };

    renderDropdown = () => {
        const showDropdown =
            this.props.shouldShowDropdown && this.state.isDropdownOpen;

        return (
            showDropdown && (
                <ul className="dropdown-options">
                    {this.state.suggestions.map(this.renderDropdownItem)}
                </ul>
            )
        );
    };

    renderSelectedTag = ({ label, value }, index) => {
        return (
            <Tag key={`tag-${index}`} onRemove={this.handleOptionRemove(value)}>
                {label}
            </Tag>
        );
    };

    render() {
        return (
            <div
                className="component-Elements-MultiSelectDropdown"
                ref={this.setWrapperRef}
            >
                <div className="input-container">
                    {this.state.selectedOptions.map(this.renderSelectedTag)}
                    <InputField
                        name="job-type"
                        placeholder="Select job type"
                        inputValue={this.state.value}
                        onChange={this.handleChange}
                        onFocus={this.handleOpenDropdown}
                        onBlur={this.handleOpenDropdown}
                        onKeyDown={this.handleKeyDown}
                        autocomplete="none"
                    />
                </div>
                {this.renderDropdown()}
            </div>
        );
    }
}
